import { uniq } from "lodash";
import { CreditDetails, DesignDecoration } from "../types";
import { TFunction } from "i18next";

/**
 * Formats the display text for different types of credit details
 */
export const getFormattedCreditDetails = (
  creditsDetails: CreditDetails[],
  t: TFunction,
  productName: string
) => {
  // Separate non-color credits
  const nonColorCredits =
    creditsDetails
      .filter((c) => !["firstColor", "extraColor"].includes(c.explanation))
      .map((c) => ({
        ...c,
        name: getCreditDetailsDisplayText(t, c, productName),
      })) ?? [];

  // Process color-related credits
  const rawColorCredits =
    creditsDetails.filter((c) =>
      ["firstColor", "extraColor"].includes(c.explanation)
    ) ?? [];

  // Group colors by fabric type
  const fabricTypes = uniq(rawColorCredits.map((c) => c.fabric));

  const colors = fabricTypes.map((fabric) => {
    const fabricColors = rawColorCredits.filter((c) => c.fabric === fabric);

    // Combine credits for the same fabric type
    return fabricColors.reduce(
      (acc, curr) => ({
        ...acc,
        credits: acc.credits + curr.credits,
        name: t("pricing.creditType.colors", {
          count: fabricColors.length,
          fabricType: fabric,
        }),
      }),
      {
        ...fabricColors[0],
        name: t("pricing.creditType.colors", {
          count: 1,
          fabricType: fabric,
        }),
      }
    );
  });

  return [...colors, ...nonColorCredits].filter(Boolean);
};

const getCreditDetailsDisplayText = (
  t: TFunction,
  creditDetail: CreditDetails,
  productName: string
) => {
  if (!productName || !creditDetail.displayLabels) {
    return "";
  }

  const { displayLabels } = creditDetail;

  switch (creditDetail.type) {
    case "ARTWORK":
      if (displayLabels[0] && displayLabels[2]) {
        return `${t(`specs.decoration_types.${displayLabels[0]}.title`)} - ${t(`positions.${displayLabels[2]}`)}`;
      }
      return "-Artwork-";
    case "Optional layer":
      if (displayLabels[3] && displayLabels[0]) {
        return `${t(`specs.${productName}.${displayLabels[3]}`)} - ${t(`specs.${productName}.${displayLabels[0]}`)} `;
      }
      return "-Optional layer-";
    case "Style":
      if (displayLabels[2]) {
        return t(`specs.${productName}.${displayLabels[2]}`);
      }
      return "-Style-";
    default:
      return "-Unknown-";
  }
};

export const containsCustomLabel = (decorations: DesignDecoration[]) => {
  return decorations.some(
    (decoration) => decoration.type === "customLabelRI2025"
  );
};
